import { service } from "@ember/service";
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from "@ember/object";
import { task } from "ember-concurrency";
import groupBy from "glesys-controlpanel/utils/group-by";
import { tracked } from "@glimmer/tracking";
import { modifier } from "ember-modifier";
import DnsDomainController from "../controller";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

export default class DnsDomainDnsRecordsController extends DnsDomainController {
  @service intl;
  @service keyboard;
  @service router;
  @service store;

  @tracked records = [];
  @tracked servers = [];

  get stateColor() {
    let state = this.state;
    if (state === "glesys-ok") {
      return "green";
    }
    if (state === "glesys-failed") {
      return "red";
    }
    return "grey";
  }

  // This needs to be computed,
  // otherwise we get weird rerenders where the input loses focus and it scrolls down to the bottom
  // Is there another way to fix it?
  @computed("intl.primaryLocale", "records.[]")
  get sortedRecords() {
    let byType = (a, b) => a.type.localeCompare(b.type);
    let newFirst = (a, b) => b.isNew - a.isNew;
    let byHostname = (a, b) =>
      a.isNotNew && b.isNotNew ? a.host?.localeCompare(b.host, this.intl.primaryLocale, { numeric: true }) : 0;

    return this.records.slice().sort((a, b) => byType(a, b) || byHostname(a, b) || newFirst(a, b));
  }

  get groupedRecords() {
    return groupBy(this.sortedRecords, "type");
  }

  get dirtyRecords() {
    return this.records.filter((record) => record.hasDirtyAttributes || record.isDeleted);
  }

  get hasDirtyRecords() {
    return this.dirtyRecords.length > 0;
  }

  get isDirty() {
    return this.hasDirtyRecords || this.domain.hasDirtyAttributes;
  }

  get serverArray() {
    let servers = this.servers;
    let array = servers.map((server) => {
      return {
        hostname: server.hostname,
        ipsVersion4: server.ipsVersion4.map((ip) => ip.address),
        ipsVersion6: server.ipsVersion6.map((ip) => ip.address),
      };
    });
    return array;
  }

  registerListeners = modifier(() => {
    this.keyboard.listenFor(
      { ctrlKey: true, code: "KeyS", preventDefault: true, ignoreFormFieldFocus: true },
      this.saveChanges,
    );
    this.keyboard.listenFor(
      { metaKey: true, code: "KeyS", preventDefault: true, ignoreFormFieldFocus: true },
      this.saveChanges,
    );

    return () => {
      this.keyboard.stopListenFor({ ctrlKey: true, code: "KeyS" });
      this.keyboard.stopListenFor({ metaKey: true, code: "KeyS" });
    };
  });

  onSoaChange = (event) => {
    let { name, value, type } = event.target;

    this.domain[name] = type === "number" ? Number.parseInt(value, 10) : value;
  };

  deleteDomainTask = task(async () => {
    this.domain.deleteRecord();
    await this.store.request(saveRecord(this.domain)).catch((error) => {
      this.router.transitionTo("dns.domain", this.domain.id);
      throw error;
    });
    await this.router.transitionTo("dns").followRedirects();
  });

  @action
  async saveChanges() {
    if (!this.isDirty) {
      return;
    }

    let domain = this.domain;
    let records = this.dirtyRecords;

    if (domain.hasDirtyAttributes) {
      this.store.request(saveRecord(domain));
    }
    try {
      await Promise.all(records.map((record) => this.store.request(saveRecord(record))));
      this.notification.add("success", this.intl.t("dns.updated.title"), this.intl.t("dns.updated.content"));
    } catch {
      this.notification.add("error", "", this.intl.t("dns.updated.error"));
    }
  }

  @action
  discardChanges() {
    let domain = this.domain;
    let records = this.dirtyRecords;

    domain.rollbackAttributes();
    records.forEach((record) => record.rollbackAttributes());
  }

  @action
  template(template) {
    let domain = this.domain;

    template.records.forEach((record) => {
      this.store.createRecord("dns-record", Object.assign(record, { domain }));
    });
  }

  loadRecords = task(async () => {
    [this.records, this.servers] = await Promise.all([this.domain.records, this.project.get("servers")]);
  });
}
